import React, { useRef, useState, useEffect } from 'react';
import {
  TextField,
  TextFieldProps,
  Box,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Sparkles,
  RotateCcw,
  LucideIcon,
  AudioLines,
  Play,
  Square,
  X,
  Copy,
} from 'lucide-react';

type WidthVariant = 'full' | 'medium' | 'small';

// Global state for managing single audio playback
let currentlyPlayingAudioElement: HTMLAudioElement | null = null;

interface FormTextFieldProps extends Omit<TextFieldProps, 'helperText'> {
  width?: WidthVariant;
  showCount?: boolean;
  tip?: string;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
  regenerateTitle?: string;
  regenerateIcon?: LucideIcon;
  regenerateDisabled?: boolean; // Issue #643: Disable regenerate button independently from field
  onReset?: () => void;
  helperText?: React.ReactNode;
  rows?: number;
  audioUrl?: string | null;
  useFallbackIcon?: boolean;
  onDelete?: () => void | Promise<void>;
  isDeleting?: boolean;
  onCopy?: () => void | Promise<void>; // Copy field content or template
  isCopying?: boolean;
  copyTitle?: string;
}

const getWidthStyles = (width: WidthVariant) => {
  switch (width) {
    case 'small':
      return { width: 250 };
    case 'medium':
      return { width: 'fit-content', minWidth: 300 };
    case 'full':
    default:
      return { width: '100%' };
  }
};

export const FormTextField: React.FC<FormTextFieldProps> = ({
  width = 'full',
  showCount = false,
  tip,
  onRegenerate,
  isRegenerating = false,
  regenerateTitle = 'Regenerate',
  regenerateIcon: RegenerateIcon = Sparkles,
  regenerateDisabled = false,
  onReset,
  value = '',
  helperText: externalHelperText,
  rows = 1,
  sx,
  audioUrl,
  useFallbackIcon = false,
  onDelete,
  isDeleting = false,
  onCopy,
  isCopying = false,
  copyTitle = 'Copy',
  ...props
}) => {
  const widthStyles = getWidthStyles(width);
  const isMultiline = rows > 1;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Set up audio event handlers
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return undefined;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    audioElement.addEventListener('ended', handleEnded);

    // eslint-disable-next-line consistent-return
    return () => {
      audioElement.removeEventListener('ended', handleEnded);
    };
  }, []);

  const handlePlayPause = () => {
    const audioElement = audioRef.current;
    if (!audioElement || !audioUrl) return;

    if (isPlaying) {
      // Stop and reset
      audioElement.pause();
      audioElement.currentTime = 0;
      setIsPlaying(false);
      if (currentlyPlayingAudioElement === audioElement) {
        currentlyPlayingAudioElement = null;
      }
    } else {
      // Stop any other currently playing audio
      if (
        currentlyPlayingAudioElement &&
        currentlyPlayingAudioElement !== audioElement
      ) {
        currentlyPlayingAudioElement.pause();
        currentlyPlayingAudioElement.currentTime = 0;
      }

      // Play this audio
      audioElement.play();
      setIsPlaying(true);
      currentlyPlayingAudioElement = audioElement;
    }
  };

  // Determine which icon to show for audio player
  const getAudioIcon = () => {
    if (isPlaying) {
      return <Square size={20} />;
    }
    return useFallbackIcon ? <AudioLines size={20} /> : <Play size={20} />;
  };

  const helperText = React.useMemo(() => {
    if (externalHelperText) {
      return externalHelperText;
    }

    const count = showCount ? `${String(value).length} characters` : '';
    const tipText = tip ? <strong>{tip}</strong> : '';

    if (count && tip) {
      return (
        <>
          {count} <strong>{tip}</strong>
        </>
      );
    }

    return count || tipText || undefined;
  }, [showCount, value, tip, externalHelperText]);

  return (
    <Box
      sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', ...widthStyles }}
    >
      <TextField
        {...props}
        value={value}
        helperText={helperText}
        multiline={isMultiline}
        rows={isMultiline ? rows || 2 : undefined}
        sx={{
          width: '100%',
          ...(isMultiline && {
            '& .MuiInputBase-root textarea': {
              resize: 'vertical',
            },
          }),
          ...sx,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {onCopy && (
                <IconButton
                  onClick={onCopy}
                  disabled={Boolean(isCopying || props.disabled)}
                  title={copyTitle}
                  size="small"
                >
                  {isCopying ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Copy size={20} />
                  )}
                </IconButton>
              )}
              {audioUrl && (
                <IconButton
                  onClick={handlePlayPause}
                  disabled={!audioUrl}
                  title={isPlaying ? 'Stop' : 'Play'}
                  size="small"
                >
                  {getAudioIcon()}
                </IconButton>
              )}
              {onDelete && audioUrl && (
                <IconButton
                  onClick={onDelete}
                  disabled={Boolean(isDeleting || props.disabled)}
                  title="Delete audio file"
                  size="small"
                >
                  {isDeleting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <X size={20} />
                  )}
                </IconButton>
              )}
              {onRegenerate && (
                <IconButton
                  onClick={onRegenerate}
                  disabled={Boolean(isRegenerating || regenerateDisabled || props.disabled)}
                  title={regenerateTitle}
                >
                  {isRegenerating ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RegenerateIcon />
                  )}
                </IconButton>
              )}
              {onReset && (
                <IconButton
                  onClick={onReset}
                  disabled={Boolean(props.disabled)}
                  title="Reset"
                >
                  <RotateCcw />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      {audioUrl && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio ref={audioRef} src={audioUrl} style={{ display: 'none' }} />
      )}
    </Box>
  );
};
